import supabase from './supabase';

// ── log_entries ──────────────────────────────────────────────────
const LOG_COLS = [
  'id','date','trip_type','title','departure','stopover','destination','dest_type','dest_name',
  'depart_time','arrive_time','total_time','nm','moving_hrs','motor_hrs','sail_hrs',
  'sail_pct','log_nm','max_sog','avg_sog','max_wind','avg_wind','time_at_sea',
  'engine_start','engine_end','avg_rpm','fuel_est','gen_start','gen_end',
  'fuel_gal','fuel_ppg','fuel_location','fuel_eng_hrs',
  'fuel_t1_start','fuel_t1_end','fuel_t2_start','fuel_t2_end',
  'batt_start','batt_end','water_aft_start','water_aft_end','water_used','water_topoff',
  'chk_oil','chk_strainer','chk_bilge','chk_fuel_added','oil_qty','oil_eng_hrs',
  'wind_start','baro_start','baro_trend','visibility','temp','wind_end','baro_end',
  'sail_main','sail_headsail','sail_spin','sail_mode',
  'captain','crew','guests','solo','overnight','nights','next_depart_date','overnight_notes',
  'race','difficulty','stars','comments','photo_url','photos',
  'boat','boat_id','boat_type','start_lat','start_lon','end_lat','end_lon','track',
  'max_stw','avg_stw','avg_twd','twd_stddev','avg_polar','max_polar','max_current','avg_current','current_dir','depth','stationary_hrs','batt_min',
  'arrival','anchoring','guest_sailing','wind',
  'imported','import_batch',
  'voyage_id',
];

const NUM_COLS = new Set([
  'nm','moving_hrs','motor_hrs','sail_hrs','sail_pct','log_nm',
  'max_sog','avg_sog','max_wind','avg_wind','time_at_sea',
  'engine_start','engine_end','avg_rpm','fuel_est','gen_start','gen_end',
  'fuel_gal','fuel_ppg','fuel_eng_hrs',
  'fuel_t1_start','fuel_t1_end','fuel_t2_start','fuel_t2_end',
  'batt_start','batt_end','water_aft_start','water_aft_end','water_used',
  'oil_qty','oil_eng_hrs','baro_start','baro_end','temp',
  'start_lat','start_lon','end_lat','end_lon','nights','stars',
  'max_stw','avg_stw','avg_twd','twd_stddev','avg_polar','max_polar','max_current','avg_current','current_dir','depth','stationary_hrs','batt_min',
]);

function cleanEntry(raw) {
  const row = {};
  for (const col of LOG_COLS) {
    if (raw[col] === undefined) continue;
    if (raw[col] === '' || raw[col] === null) { row[col] = null; continue; }
    if (NUM_COLS.has(col)) {
      const n = Number(raw[col]);
      row[col] = isNaN(n) ? null : n;
    } else {
      row[col] = raw[col];
    }
  }
  row.id = raw.id || ('log_' + Date.now());
  row.boat = raw.boat || 'PMF SO45';
  return row;
}

export async function getLogEntries() {
  const { data, error } = await supabase
    .from('log_entries')
    .select('*')
    .order('date', { ascending: false });
  if (error) throw new Error('getLogEntries: ' + error.message);
  return data || [];
}

export async function upsertLogEntry(raw) {
  const row = cleanEntry(raw);
  const { data, error } = await supabase
    .from('log_entries')
    .upsert(row)
    .select()
    .single();
  if (error) throw new Error('upsertLogEntry: ' + error.message);
  return data;
}

export async function deleteLogEntry(id) {
  const { error } = await supabase.from('log_entries').delete().eq('id', id);
  if (error) throw new Error('deleteLogEntry: ' + error.message);
}

// ── maintenance_checks ───────────────────────────────────────────
export async function getMaintenanceChecks() {
  const { data, error } = await supabase
    .from('maintenance_checks')
    .select('*')
    .order('date', { ascending: false });
  if (error) throw new Error('getMaintenanceChecks: ' + error.message);
  return (data || []).map(expandCheck);
}

export async function upsertMaintenanceCheck(raw) {
  const row = flattenCheck(raw);
  const { data, error } = await supabase
    .from('maintenance_checks')
    .upsert(row)
    .select()
    .single();
  if (error) throw new Error('upsertMaintenanceCheck: ' + error.message);
  return expandCheck(data);
}

export async function deleteMaintenanceCheck(id) {
  const { error } = await supabase.from('maintenance_checks').delete().eq('id', id);
  if (error) throw new Error('deleteMaintenanceCheck: ' + error.message);
}

function flattenCheck(e) {
  const eng  = e.engine  || {};
  const gen  = e.genset  || {};
  const heat = e.heater  || {};
  return {
    id:                   e.id || ('chk_' + Date.now()),
    date:                 e.date,
    performed_by:         e.performed_by || 'Frederick',
    from_log:             e.from_log || null,
    notes:                e.notes || null,
    eng_oil_checked:      !!eng.oil_checked,
    eng_oil_qty:          eng.oil_qty  ? Number(eng.oil_qty)  : null,
    eng_oil_hrs:          eng.oil_hrs  ? Number(eng.oil_hrs)  : null,
    eng_belt_checked:     !!eng.belt_checked,
    eng_strainer_cleaned: !!eng.strainer_cleaned,
    gen_oil_checked:      !!gen.oil_checked,
    gen_oil_qty:          gen.oil_qty  ? Number(gen.oil_qty)  : null,
    gen_oil_hrs:          gen.oil_hrs  ? Number(gen.oil_hrs)  : null,
    gen_belt_checked:     !!gen.belt_checked,
    gen_strainer_cleaned: !!gen.strainer_cleaned,
    heat_filter_cleaned:   !!heat.filter_cleaned,
    heat_combustion_check: !!heat.fuel_line,
    heat_annual_service:   !!heat.annual_service,
    custom_item:          e.custom_item   || null,
    custom_hrs:           e.custom_hrs    ? Number(e.custom_hrs)  : null,
    custom_cost:          e.custom_cost   ? Number(e.custom_cost) : null,
    custom_vendor:        e.custom_vendor || null,
    custom_notes:         e.custom_notes  || null,
  };
}

function expandCheck(r) {
  return {
    ...r,
    engine: { oil_checked:r.eng_oil_checked, oil_qty:r.eng_oil_qty, oil_hrs:r.eng_oil_hrs, belt_checked:r.eng_belt_checked, strainer_cleaned:r.eng_strainer_cleaned },
    genset:  { oil_checked:r.gen_oil_checked, oil_qty:r.gen_oil_qty, oil_hrs:r.gen_oil_hrs, belt_checked:r.gen_belt_checked, strainer_cleaned:r.gen_strainer_cleaned },
    heater:  { filter_cleaned:r.heat_filter_cleaned, fuel_line:r.heat_combustion_check, annual_service:r.heat_annual_service },
  };
}

// ── boat_settings (singleton) ────────────────────────────────────
export async function getBoatSettings() {
  const { data, error } = await supabase
    .from('boat_settings')
    .select('*')
    .eq('id', 'pmf_so45')
    .maybeSingle();
  if (error) throw new Error('getBoatSettings: ' + error.message);
  return data;
}

export async function upsertBoatSettings(settings) {
  const { data, error } = await supabase
    .from('boat_settings')
    .upsert({ ...settings, id: 'pmf_so45', updated_at: new Date().toISOString() })
    .select()
    .single();
  if (error) throw new Error('upsertBoatSettings: ' + error.message);
  return data;
}

// ── service_records ──────────────────────────────────────────
export async function getServiceRecords() {
  const { data, error } = await supabase
    .from('service_records')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error('getServiceRecords: ' + error.message);
  return data || [];
}

export async function upsertServiceRecord(record) {
  const row = {
    id:           record.id || ('svc_' + Date.now()),
    item_id:      record.item_id,
    item_name:    record.item_name,
    system:       record.system,
    service_date: record.service_date || new Date().toISOString().slice(0,10),
    service_hrs:  record.service_hrs  ? Number(record.service_hrs)  : null,
    cost:         record.cost         ? Number(record.cost)         : null,
    vendor:       record.vendor       || null,
    notes:        record.notes        || null,
  };
  const { data, error } = await supabase
    .from('service_records')
    .upsert(row)
    .select()
    .single();
  if (error) throw new Error('upsertServiceRecord: ' + error.message);
  return data;
}

export async function deleteServiceRecord(id) {
  const { error } = await supabase
    .from('service_records')
    .delete()
    .eq('id', id);
  if (error) throw new Error('deleteServiceRecord: ' + error.message);
}

// ── fuel_log ─────────────────────────────────────────────────
export async function getFuelRecords() {
  const { data, error } = await supabase
    .from('fuel_log')
    .select('*')
    .order('date', { ascending: false });
  if (error) throw new Error('getFuelRecords: ' + error.message);
  return data || [];
}

export async function upsertFuelRecord(record) {
  const row = {
    id:             record.id || ('fuel_' + Date.now()),
    date:           record.date,
    location:       record.location || null,
    engine_hrs:     record.engine_hrs     ? Number(record.engine_hrs)     : null,
    gen_hrs:        record.gen_hrs        ? Number(record.gen_hrs)        : null,
    main_gal:       record.main_gal       ? Number(record.main_gal)       : null,
    aux_gal:        record.aux_gal        ? Number(record.aux_gal)        : null,
    total_gal:      record.total_gal      ? Number(record.total_gal)      : null,
    total_lit:      record.total_lit      ? Number(record.total_lit)      : null,
    price_per_gal:  record.price_per_gal  ? Number(record.price_per_gal)  : null,
    total_cost:     record.total_cost     ? Number(record.total_cost)     : null,
    gal_per_hr:     record.gal_per_hr     ? Number(record.gal_per_hr)     : null,
    hrs_since_last: record.hrs_since_last ? Number(record.hrs_since_last) : null,
    unit:           record.unit || 'gal',
    notes:          record.notes || null,
  };
  const { data, error } = await supabase
    .from('fuel_log')
    .upsert(row)
    .select()
    .single();
  if (error) throw new Error('upsertFuelRecord: ' + error.message);
  return data;
}

export async function deleteFuelRecord(id) {
  const { error } = await supabase.from('fuel_log').delete().eq('id', id);
  if (error) throw new Error('deleteFuelRecord: ' + error.message);
}

// ── fuel_log ──────────────────────────────────────────────────
export async function getFuelLog() {
  const { data, error } = await supabase
    .from('fuel_log')
    .select('*')
    .order('date', { ascending: false });
  if (error) throw new Error('getFuelLog: ' + error.message);
  return data || [];
}

export async function upsertFuelEntry(raw) {
  const row = {
    id:           raw.id || ('fuel_' + Date.now()),
    date:         raw.date,
    location:     raw.location || null,
    engine_hrs:   raw.engine_hrs   ? Number(raw.engine_hrs)   : null,
    gen_hrs:      raw.gen_hrs      ? Number(raw.gen_hrs)      : null,
    main_gal:     raw.main_gal     ? Number(raw.main_gal)     : null,
    aux_gal:      raw.aux_gal      ? Number(raw.aux_gal)      : null,
    total_gal:    raw.total_gal    ? Number(raw.total_gal)    : null,
    main_level:   raw.main_level  != null && raw.main_level !== '' ? Number(raw.main_level) : null,
    aux_level:    raw.aux_level   != null && raw.aux_level  !== '' ? Number(raw.aux_level)  : null,
    price_per_gal:raw.price_per_gal? Number(raw.price_per_gal): null,
    total_cost:   raw.total_cost   ? Number(raw.total_cost)   : null,
    gal_per_hr:   raw.gal_per_hr   ? Number(raw.gal_per_hr)   : null,
    hrs_since_last:raw.hrs_since_last ? Number(raw.hrs_since_last) : null,
    notes:        raw.notes || null,
  };
  const { data, error } = await supabase
    .from('fuel_log')
    .upsert(row)
    .select()
    .single();
  if (error) throw new Error('upsertFuelEntry: ' + error.message);
  return data;
}

export async function deleteFuelEntry(id) {
  const { error } = await supabase.from('fuel_log').delete().eq('id', id);
  if (error) throw new Error('deleteFuelEntry: ' + error.message);
}

// ── photos (Supabase Storage, public bucket "photos") ────────────
const PHOTO_BUCKET = 'photos';

// Read a File's bytes into an in-memory Blob, retrying with backoff. On iOS a
// photo that lives in iCloud is handed to the file input as a thumbnail-backed
// reference before the full-res original has downloaded, so the first read can
// throw (NotReadableError) or return empty/partial bytes — which is why the
// upload used to fail on the first try and work on the second. Reading the bytes
// forces and awaits the iCloud download; we retry until we actually have them.
async function readReadyBlob(file, tries = 5) {
  for (let i = 0; i < tries; i++) {
    try {
      const buf = await file.arrayBuffer();
      if (buf && buf.byteLength > 1024) {        // a real photo is well over 1 KB
        return new Blob([buf], { type: file.type || 'image/jpeg' });
      }
    } catch (e) { /* original not down from iCloud yet */ }
    await new Promise(r => setTimeout(r, 500 * (i + 1)));   // 0.5s, 1s, 1.5s, 2s…
  }
  throw new Error('Photo is still downloading from iCloud — please try again in a moment.');
}

// Downscale a large phone photo before upload: cap the long edge and re-encode
// as JPEG. A 4000px, 6 MB original becomes ~1600px and a few hundred KB, which
// is the single biggest win for feed and carousel performance.
// Decodes via an <img> + object URL rather than createImageBitmap, because iOS
// Safari cannot decode HEIC through createImageBitmap (it throws) — the <img>
// path uses the system decoder, so HEIC is converted to JPEG reliably.
function loadImageEl(src) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(src);
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image decode failed')); };
    img.src = url;
  });
}

async function downscaleImage(file, maxEdge = 1600, quality = 0.82) {
  const isHeic = /image\/heic|image\/heif/i.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || '');
  // Non-image and already-small non-HEIC files pass through untouched.
  if (!isHeic && file.type && !file.type.startsWith('image/')) return file;
  // Force the iCloud download and get real bytes before doing anything else.
  const blob = await readReadyBlob(file);
  let ref;
  try {
    ref = await loadImageEl(blob);
    const { img, url } = ref;
    const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    if (!iw || !ih) { URL.revokeObjectURL(url); return new File([blob], (file.name.replace(/\.[^.]+$/, '') || 'photo') + '.jpg', { type: blob.type || 'image/jpeg' }); }
    const scale = Math.min(1, maxEdge / Math.max(iw, ih));
    // Skip re-encode only when it's already small AND not HEIC (HEIC must convert).
    if (scale >= 1 && !isHeic && blob.size < 900 * 1024) { URL.revokeObjectURL(url); return new File([blob], file.name || 'photo', { type: blob.type || 'image/jpeg' }); }
    const w = Math.round(iw * scale), h = Math.round(ih * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
    URL.revokeObjectURL(url);
    const out = await new Promise(res => canvas.toBlob(res, 'image/jpeg', quality));
    if (!out) return new File([blob], (file.name.replace(/\.[^.]+$/, '') || 'photo') + '.jpg', { type: blob.type || 'image/jpeg' });
    return new File([out], (file.name.replace(/\.[^.]+$/, '') || 'photo') + '.jpg', { type: 'image/jpeg' });
  } catch {
    if (ref?.url) URL.revokeObjectURL(ref.url);
    // Decode failed but we have real bytes — upload those with correct type.
    return new File([blob], file.name || 'photo', { type: blob.type || file.type || 'image/jpeg' });
  }
}

export async function uploadPhoto(file, entryDate) {
  const small = await downscaleImage(file);
  // Use the actual resulting type — normally image/jpeg after downscale, but if
  // conversion fell back to the original, label it correctly rather than lying.
  const type = small.type || 'image/jpeg';
  const ext = /jpeg|jpg/i.test(type) ? 'jpg' : (type.split('/')[1] || 'jpg').replace('+xml', '');
  const safe = `${entryDate || 'undated'}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from(PHOTO_BUCKET).upload(safe, small, {
    cacheControl: '31536000', // a year: photos never change
    upsert: false,
    contentType: type,
  });
  if (error) throw new Error('uploadPhoto: ' + error.message);
  const { data } = supabase.storage.from(PHOTO_BUCKET).getPublicUrl(safe);
  return { url: data.publicUrl, path: safe };
}

export async function deletePhoto(path) {
  if (!path) return;
  const { error } = await supabase.storage.from(PHOTO_BUCKET).remove([path]);
  if (error) throw new Error('deletePhoto: ' + error.message);
}

// ── water_log ────────────────────────────────────────────────────
// Gauge reads litres USED since the last top-off. 0 = full, up to 445 L
// (210 fore + 235 aft). A top-off is a reading of 0.
export const WATER_CAPACITY_L = 445;

export async function getWaterLog() {
  const { data, error } = await supabase
    .from('water_log')
    .select('*')
    .order('date', { ascending: false });
  if (error) throw new Error('getWaterLog: ' + error.message);
  return data || [];
}

export async function upsertWaterEntry(record) {
  const row = {
    id:          record.id || ('water_' + Date.now()),
    date:        record.date,
    kind:        record.kind || 'reading',
    liters_used: record.liters_used != null && record.liters_used !== '' ? Number(record.liters_used) : null,
    location:    record.location || null,
    note:        record.note || null,
  };
  const { data, error } = await supabase
    .from('water_log')
    .upsert(row)
    .select()
    .single();
  if (error) throw new Error('upsertWaterEntry: ' + error.message);
  return data;
}

export async function deleteWaterEntry(id) {
  const { error } = await supabase.from('water_log').delete().eq('id', id);
  if (error) throw new Error('deleteWaterEntry: ' + error.message);
}

// ── voyages ──────────────────────────────────────────────────────
// A voyage is a named collection of log entries (one voyage per entry).
export async function getVoyages() {
  const { data, error } = await supabase
    .from('voyages')
    .select('*')
    .order('start_date', { ascending: false });
  if (error) throw new Error('getVoyages: ' + error.message);
  return data || [];
}

export async function upsertVoyage(raw) {
  const row = {
    id:         raw.id || ('voy_' + Date.now()),
    name:       raw.name,
    start_date: raw.start_date || null,
    end_date:   raw.end_date || null,
    cover_url:  raw.cover_url || null,
    writeup:    raw.writeup || null,
  };
  const { data, error } = await supabase
    .from('voyages')
    .upsert(row)
    .select()
    .single();
  if (error) throw new Error('upsertVoyage: ' + error.message);
  return data;
}

export async function deleteVoyage(id) {
  // Unassign its entries first so nothing is orphaned or deleted with it.
  const { error: e1 } = await supabase.from('log_entries').update({ voyage_id: null }).eq('voyage_id', id);
  if (e1) throw new Error('deleteVoyage (unassign): ' + e1.message);
  const { error } = await supabase.from('voyages').delete().eq('id', id);
  if (error) throw new Error('deleteVoyage: ' + error.message);
}

// Assign a set of entry ids to a voyage (or null to remove). Returns nothing;
// caller refreshes entries from state.
export async function setEntriesVoyage(entryIds, voyageId) {
  if (!Array.isArray(entryIds) || !entryIds.length) return;
  const { error } = await supabase
    .from('log_entries')
    .update({ voyage_id: voyageId })
    .in('id', entryIds);
  if (error) throw new Error('setEntriesVoyage: ' + error.message);
}

// ── boats (multi-boat fleet) ─────────────────────────────────────
export async function getBoats() {
  const { data, error } = await supabase
    .from('boats')
    .select('*')
    .eq('active', true)
    .order('sort_order', { ascending: true });
  if (error) throw new Error('getBoats: ' + error.message);
  return data || [];
}
